import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';

import * as monthUtils from '@actual-app/core/shared/months';
import { css, keyframes } from '@emotion/css';

import { LARGO_RESPUESTA } from '#components/mobile/budget/recuerdos';
import type { Recuerdo } from '#components/mobile/budget/recuerdos';
import {
  aspectoDeRecuerdo,
  textoRitmo,
  textoSello,
} from '#components/mobile/budget/recuerdosAspecto';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useReducedMotion } from '#hooks/useReducedMotion';

import { Botella } from './Botella';
import { claseEscena, e, GRANO, SERIF, trazoOla } from './escenaCss';
import {
  abiertaPorOtro,
  CARTA,
  disposicion,
  escalaEscenario,
  ESCENARIO,
  finDe,
  HORIZONTE,
  INICIO_ABREVIADA,
  lineaGuardar,
  lineaLlegada,
  modoLlegada,
  pieCarta,
  vibracionesGuardar,
  vibracionesLlegada,
  X_BOTELLA,
  X_CENTRO,
  Y_ACCIONES,
  Y_CARTELA,
} from './escenaLlegada';
import type { Parte, Paso, Vibracion } from './escenaLlegada';
import { GaviTinta } from './GaviTinta';
import { chispa, reflejoAstro, rumbosCielo } from './geometria';
import { Sello } from './Sello';
import { useNoche } from './useNoche';

/** Por encima de las hojas y de la barra de pestañas. */
export const LLEGADA_Z_INDEX = 300;

type LlegadaBotellaProps = {
  recuerdo: Recuerdo;
  /** Id de este dispositivo. */
  disp: string;
  /** «Solo ver»: no se escribe nada (ni se guarda ni se responde). */
  soloLectura: boolean;
  /** Se abrió con un toque (la vibración solo va tras un gesto). */
  conGesto: boolean;
  /** `AAAA-MM-DD` (por defecto, hoy). */
  hoy?: string;
  /** «Guardar en la bitácora»: marca la botella como abierta aquí. */
  onGuardar: () => Promise<boolean> | void;
  /** «Responder a quien zarpó». */
  onResponder: (texto: string) => Promise<boolean>;
  onVerBitacora: () => void;
  onCerrar: () => void;
};

type Fase = 'llegada' | 'guardada';

/**
 * La llegada de la botella a pantalla completa (F1 §2 «Al cumplir»): mar que
 * sube, botella que emerge, Gavi a tinta «Ha llegado.», cartela con la cifra
 * y el tiempo, corcho, lacre que se rompe, carta que sale del cuello y
 * chispa de latón. Un toque salta al final. Con «reducir movimiento», la
 * composición final directa. Solo `transform` y `opacity`.
 */
export function LlegadaBotella({
  recuerdo: r,
  disp,
  soloLectura,
  conGesto,
  hoy = monthUtils.currentDay(),
  onGuardar,
  onResponder,
  onVerBitacora,
  onCerrar,
}: LlegadaBotellaProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const reducido = useReducedMotion();
  const noche = useNoche();

  const raiz = useRef<HTMLDivElement>(null);
  const cartaRef = useRef<HTMLElement>(null);
  const [altoCarta, setAltoCarta] = useState(300);
  const [escala, setEscala] = useState(() =>
    escalaEscenario(window.innerWidth, window.innerHeight),
  );
  const [fase, setFase] = useState<Fase>('llegada');
  const [listo, setListo] = useState(false);
  const [finListo, setFinListo] = useState(false);
  const [respondiendo, setRespondiendo] = useState(false);
  const [respuesta, setRespuesta] = useState('');
  const [respondida, setRespondida] = useState(false);

  const otro = abiertaPorOtro(r, disp);
  const [modo] = useState(() =>
    modoLlegada({ reducido, abiertaPorOtro: otro }),
  );
  const final = modo === 'final';
  const conSello = !otro && !soloLectura;

  const llegada = r.llegada;
  const fechaLlegada = llegada?.fecha ?? hoy;
  const aspecto = aspectoDeRecuerdo(r, hoy);
  const d = disposicion(aspecto, altoCarta);
  const destellos = chispa(aspecto.semilla);
  const pie = pieCarta(r, disp, hoy, soloLectura);
  const fecha = (dia: string, patron = 'd MMM yyyy') =>
    monthUtils.format(dia, patron, locale);
  const meta = llegada?.meta ?? r.base.metaAlZarpar;
  const cifra = format(
    meta,
    meta % 100 === 0 ? 'financial-no-decimals' : 'financial',
  );

  useEffect(() => {
    const ajustar = () =>
      setEscala(escalaEscenario(window.innerWidth, window.innerHeight));
    window.addEventListener('resize', ajustar);
    return () => window.removeEventListener('resize', ajustar);
  }, []);

  // La coreografía (o la composición final directa con movimiento reducido).
  const reproduccion = useRef<Reproduccion | null>(null);
  const arrancada = useRef(false);
  useLayoutEffect(() => {
    if (arrancada.current) {
      return;
    }
    arrancada.current = true;
    // Alto real de la carta (una sola vez, antes de pintar).
    const medido = cartaRef.current?.offsetHeight ?? 0;
    const alto = medido > 0 ? medido : altoCarta;
    setAltoCarta(alto);
    if (final || !raiz.current) {
      if (conGesto) {
        programarVibraciones(vibracionesLlegada('final'), 0);
      }
      setListo(true);
      return;
    }
    const desde = modo === 'abreviada' ? INICIO_ABREVIADA : 0;
    const rep = reproducir(
      raiz.current,
      lineaLlegada(disposicion(aspecto, alto), destellos),
      desde,
    );
    reproduccion.current = rep;
    if (conGesto) {
      rep.temporizadores.push(
        ...programarVibraciones(vibracionesLlegada(modo), 0),
      );
    }
    void rep.terminada.then(() => setListo(true));
    // La disposición se mide una vez: la coreografía no se rehace.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => reproduccion.current?.cancelar(), []);

  const saltar = () => {
    reproduccion.current?.terminar();
    if (fase === 'llegada') {
      setListo(true);
    } else {
      setFinListo(true);
    }
  };

  const guardar = () => {
    if (fase !== 'llegada') {
      return;
    }
    reproduccion.current?.terminar();
    setFase('guardada');
    setRespondiendo(false);
    if (!soloLectura) {
      void onGuardar();
    }
    const vibraciones = conGesto ? vibracionesGuardar(final, conSello) : [];
    if (final || !raiz.current) {
      programarVibraciones(vibraciones, 0);
      setFinListo(true);
      return;
    }
    const rep = reproducir(raiz.current, lineaGuardar(conSello), 0);
    reproduccion.current = rep;
    rep.temporizadores.push(...programarVibraciones(vibraciones, 0));
    void rep.terminada.then(() => setFinListo(true));
  };

  const responder = async () => {
    const texto = respuesta.trim();
    if (!texto) {
      setRespondiendo(false);
      return;
    }
    if (await onResponder(texto)) {
      setRespondida(true);
      setRespondiendo(false);
    }
  };

  const estilo = (parte: Parte): CSSProperties =>
    estiloParte(parte, final, fase, d.camara, altoCarta);

  const meses = aspecto.meses;
  const lineaTiempo = [
    meses < 1
      ? t('Less than a month at sea')
      : t('{{count}} months at sea', { count: meses }),
    llegada ? textoRitmo(t, llegada.diasRespectoPlazo) : null,
    llegada?.ajustada ? t('goal adjusted') : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const textoPie =
    pie.tipo === 'pendiente'
      ? t('Whoever sails this boat will open it')
      : pie.tipo === 'abierta-por' && pie.quien
        ? t('Opened by {{name}} on {{date}}', {
            name: pie.quien,
            date: fecha(pie.fecha, 'd MMMM yyyy'),
          })
        : t('Opened on {{date}}', { date: fecha(pie.fecha, 'd MMMM yyyy') });

  const nombre = [r.base.emoji, r.base.nombre].filter(Boolean).join(' ');
  const textoDelSello = textoSello(r.base.nombre, fechaLlegada);
  const ola1 = trazoOla(8, 5);
  const ola2 = trazoOla(10, 7);
  const xAstro = noche ? 315 : 113;

  return createPortal(
    <div
      ref={raiz}
      role="dialog"
      aria-modal
      aria-label={t('A bottle has arrived')}
      data-testid="llegada-botella"
      data-modo={modo}
      data-fase={fase}
      className={[
        claseEscena,
        claseLlegada,
        noche ? 'noche' : '',
        final ? 'quieta' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={saltar}
    >
      <div
        className="escenario"
        style={{
          width: ESCENARIO.ancho,
          height: ESCENARIO.alto,
          transform: `translate(-50%, -50%) scale(${escala})`,
        }}
      >
        <div
          className="e-ab sangra"
          data-parte="velo"
          style={{ background: e.velo, ...estilo('velo') }}
        />

        <div className="e-ab sangra" data-parte="cielo" style={estilo('cielo')}>
          <div
            className="e-ab"
            style={{
              left: 0,
              right: 0,
              top: 0,
              height: 2000 + HORIZONTE + 10,
              background: `linear-gradient(${e.cieloA}, ${e.cieloA} 2000px, ${e.cieloB} ${2000 + HORIZONTE}px)`,
            }}
          />
          <div
            className="e-ab"
            style={{ left: 2000, top: 2000, width: 412, height: 470 }}
          >
            <svg
              className="e-ab"
              width="412"
              height="470"
              style={{ left: 0, top: 0 }}
              aria-hidden
            >
              <g stroke={e.rumbo} strokeWidth=".8" fill="none">
                <path d={rumbosCielo(300, HORIZONTE)} />
                <circle cx="300" cy={HORIZONTE} r="64" />
                <circle cx="300" cy={HORIZONTE} r="60" strokeDasharray="1 3" />
              </g>
            </svg>
            <div
              className="e-ab"
              style={{
                left: noche ? 300 : 86,
                top: noche ? 150 : 300,
                width: noche ? 30 : 54,
                height: noche ? 30 : 54,
                borderRadius: '50%',
                background: e.astro,
                boxShadow: `0 0 60px 26px ${e.astroHalo}`,
              }}
            />
            <svg
              className="e-ab"
              style={{ left: 0, top: 380 }}
              width="412"
              height="90"
              viewBox="0 0 412 90"
              aria-hidden
            >
              <path
                d="M0 82 C40 70 70 66 110 72 S170 60 210 70 S260 78 290 74 L412 82 V90 H0Z"
                fill={e.costa2}
              />
              <path
                d="M262 82 C290 64 318 50 346 44 C370 42 392 52 412 58 V90 H262Z"
                fill={e.costa}
              />
              <path d="M347 44 l2-12h4l2 12z M348 31h6v-3h-6z" fill={e.costa} />
              <circle
                cx="351"
                cy="29.5"
                r="2.4"
                fill="#f2d48a"
                style={{ opacity: e.faro }}
              />
            </svg>
            {noche && (
              <div
                className="e-ab e-haz e-ambiente"
                style={{
                  left: 351,
                  top: 418,
                  width: 420,
                  height: 46,
                  marginTop: -23,
                }}
              />
            )}
          </div>
        </div>

        <div className="e-ab sangra" data-parte="mar" style={estilo('mar')}>
          <div
            className="e-ab"
            style={{ left: 2000, top: 2000, width: 412, height: 844 }}
          >
            <div
              className="e-ab"
              style={{
                left: -2000,
                right: -2000,
                top: HORIZONTE,
                height: 2400,
                background: `linear-gradient(${e.marA}, ${e.marB} 380px)`,
              }}
            />
            <svg
              className="e-ab"
              style={{ left: 0, top: HORIZONTE }}
              width="412"
              height="120"
              aria-hidden
            >
              <g fill={e.astro} opacity={noche ? 0.35 : 0.5}>
                {reflejoAstro(xAstro).map((b, i) => (
                  <rect
                    key={i}
                    x={b.x}
                    y={b.y}
                    width={b.ancho}
                    height="1.4"
                    rx=".7"
                  />
                ))}
              </g>
            </svg>
            <div
              className="e-ab e-ola e-ambiente"
              style={{ left: 0, top: 486, width: 824 }}
            >
              <svg width="824" height="360" aria-hidden>
                <path
                  d={ola1}
                  fill={e.marC}
                  stroke={e.cresta}
                  strokeWidth="1"
                />
              </svg>
            </div>
            <svg
              className="e-ab"
              style={{ left: -6, top: 470 }}
              width="96"
              height="80"
              viewBox="0 0 96 80"
              aria-hidden
            >
              <path d="M0 34h86v6H0z" fill={e.muelle} />
              <path
                d="M10 40h5v40h-5zM48 40h5v40h-5zM78 40h5v40h-5z"
                fill={e.muelle}
              />
              <path
                d="M40 34v-9c0-3 3-4 6-4s6 1 6 4v9z M37 22h18v-3H37z"
                fill={e.muelle}
              />
            </svg>
            <div
              className="e-ab"
              data-parte="rizo"
              style={{
                left: 136,
                top: 588,
                width: 140,
                height: 18,
                borderRadius: '50%',
                border: `1.5px solid ${e.cresta}`,
                opacity: 0,
              }}
            />
          </div>
        </div>

        <div
          className="e-ab"
          data-parte="gavi"
          style={{ left: 20, top: 452, ...estilo('gavi') }}
        >
          <GaviTinta ancho={44} />
        </div>
        <div
          className="e-ab rotulo"
          data-parte="dice"
          style={{ left: 16, top: 418, ...estilo('dice') }}
        >
          <Trans>It has arrived.</Trans>
        </div>

        <div
          className="e-ab sangra"
          data-parte="velo2"
          style={{ background: e.velo2, ...estilo('velo2') }}
        />

        <div
          className="e-ab"
          data-parte="vuelo"
          style={{
            left: X_BOTELLA,
            top: d.arriba,
            width: 120,
            height: d.lienzo,
            ...estilo('vuelo'),
          }}
        >
          <div
            data-parte="camara"
            style={{
              position: 'absolute',
              inset: 0,
              transformOrigin: '0 0',
              ...estilo('camara'),
            }}
          >
            <div
              data-parte="emerge"
              style={{ position: 'absolute', inset: 0, ...estilo('emerge') }}
            >
              <div
                className="e-cabeceo e-ambiente"
                style={{ position: 'absolute', inset: 0 }}
              >
                <div
                  className="e-vaiven e-ambiente"
                  style={{ position: 'absolute', inset: 0 }}
                >
                  <Botella
                    aspecto={aspecto}
                    escena
                    abierta={final}
                    className="pieza-botella"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="e-ab e-ola rapida e-ambiente"
          data-parte="frente"
          style={{ left: 0, top: 560, width: 824, ...estilo('frente') }}
        >
          <svg width="824" height="300" aria-hidden>
            <path
              d={ola2}
              fill={e.marD}
              fillOpacity=".86"
              stroke={e.cresta}
              strokeWidth="1"
            />
          </svg>
        </div>

        <div
          className="e-ab cartela"
          data-parte="cartela"
          style={{ top: Y_CARTELA, ...estilo('cartela') }}
        >
          <div className="cifra tnum">{cifra}</div>
          <div className="regla" />
          <div className="linea">{lineaTiempo}</div>
        </div>

        <div
          className="e-ab anillo"
          data-parte="anillo"
          style={{ left: X_CENTRO, top: d.boca }}
        />
        {destellos.map((_, i) => (
          <div
            key={i}
            className="e-ab rombo"
            data-parte={`rombo${i}`}
            style={{ left: X_CENTRO, top: d.boca }}
          />
        ))}
        <svg
          className="e-ab estrella"
          data-parte="estrella"
          style={{ left: X_CENTRO + 44, top: d.boca - 72 }}
          viewBox="-9 -9 18 18"
          aria-hidden
        >
          <path
            d="M0-9 1.6-1.6 9 0 1.6 1.6 0 9-1.6 1.6-9 0-1.6-1.6Z"
            fill={e.laton2}
          />
        </svg>

        <div
          className="e-ab marco-carta"
          style={{
            left: CARTA.x - 40,
            width: CARTA.ancho + 80,
            top: d.arribaCarta,
            height: altoCarta,
            overflow: final || listo ? 'visible' : 'hidden',
          }}
        >
          <article
            ref={cartaRef}
            className="carta"
            data-parte="carta"
            data-testid="llegada-carta"
            style={{
              boxShadow: sombraCarta(aspecto.patina),
              ...estilo('carta'),
            }}
          >
            <div className="cab">
              <span>
                <Trans>Letter from the start</Trans>
              </span>
              <span className="tnum">{fecha(r.base.zarpoEn)}</span>
            </div>
            <p className="txt">
              {r.base.carta || t('This voyage set sail without a letter.')}
            </p>
            {r.base.autor && <p className="firma">— {r.base.autor}</p>}
            {r.firmas.map(f => (
              <p key={f.disp} className="firma-otra">
                {f.texto}
                {f.quien && <span className="firma"> — {f.quien}</span>}
              </p>
            ))}
            {r.respuestas.map(f => (
              <p key={`r${f.disp}`} className="respuesta">
                {f.texto}
                {f.quien && <span> — {f.quien}</span>}
              </p>
            ))}
            <div className="pie" data-testid="llegada-pie">
              {textoPie}
            </div>
          </article>
        </div>
        <div
          className="e-ab rollo"
          data-parte="rollo"
          style={{ top: d.arribaCarta - 8, ...estilo('rollo') }}
        />

        <div
          className="e-ab acciones"
          data-parte="acciones"
          style={{
            top: Y_ACCIONES - (respondiendo ? 92 : 0),
            pointerEvents: listo && fase === 'llegada' ? 'auto' : 'none',
            ...estilo('acciones'),
          }}
          onClick={ev => ev.stopPropagation()}
        >
          {respondiendo ? (
            <div className="responder">
              <textarea
                aria-label={t('Reply to whoever set sail')}
                placeholder={t('A few words for whoever set sail')}
                maxLength={LARGO_RESPUESTA}
                value={respuesta}
                onChange={ev => setRespuesta(ev.target.value)}
                rows={2}
                autoFocus
              />
              <div className="sec">
                <button type="button" onClick={() => setRespondiendo(false)}>
                  <Trans>Cancel</Trans>
                </button>
                <button type="button" onClick={() => void responder()}>
                  <Trans>Send reply</Trans>
                </button>
              </div>
            </div>
          ) : (
            !soloLectura && (
              <div className="sec">
                {respondida ? (
                  <span className="hecho">
                    <Trans>Reply saved</Trans>
                  </span>
                ) : (
                  <button type="button" onClick={() => setRespondiendo(true)}>
                    <Trans>Reply to whoever set sail</Trans>
                  </button>
                )}
              </div>
            )
          )}
          {soloLectura ? (
            <button type="button" className="pri" onClick={onCerrar}>
              <Trans>Close</Trans>
            </button>
          ) : (
            <button
              type="button"
              className="pri"
              data-testid="llegada-guardar"
              onClick={guardar}
            >
              <Trans>Save to the logbook</Trans>
            </button>
          )}
        </div>

        <section
          className="e-ab pagina"
          data-parte="pagina"
          aria-label={t('Logbook')}
          aria-hidden={fase !== 'guardada'}
          style={estilo('pagina')}
        >
          <div className="cab">
            <span>
              <Trans>Logbook</Trans>
            </span>
          </div>
          <h2>{nombre}</h2>
          <Fila etiqueta={t('Amount')} valor={cifra} />
          <Fila etiqueta={t('Departed')} valor={fecha(r.base.zarpoEn)} />
          <Fila etiqueta={t('Arrival')} valor={fecha(fechaLlegada)} />
          {llegada?.aportaciones != null && (
            <Fila
              etiqueta={t('Contributions')}
              valor={String(llegada.aportaciones)}
            />
          )}
          <div
            className="e-ab mancha"
            data-parte="mancha"
            style={{
              background: `radial-gradient(closest-side, ${aspecto.lacre[1]}55, ${aspecto.lacre[1]}22 70%, transparent)`,
              ...estilo('mancha'),
            }}
          />
          <div
            className="e-ab sello"
            data-parte="sello"
            style={estilo('sello')}
          >
            <Sello aspecto={aspecto} texto={textoDelSello} tamano={120} />
          </div>
        </section>

        <div
          className="e-ab fin"
          data-parte="fin"
          style={{
            pointerEvents: fase === 'guardada' && finListo ? 'auto' : 'none',
            ...estilo('fin'),
          }}
          onClick={ev => ev.stopPropagation()}
        >
          <button type="button" className="pri" onClick={onVerBitacora}>
            <Trans>Open your logbook</Trans>
          </button>
          <div className="sec">
            <button type="button" onClick={onCerrar}>
              <Trans>Close</Trans>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Sombra de la carta: los bordes oscurecen con la pátina (meses de mar). */
function sombraCarta(patina: number): string {
  const borde = (10 + patina * 34).toFixed(1);
  const tinte = (0.12 + patina * 0.35).toFixed(2);
  return `0 1px 0 rgba(0,0,0,.08), 0 14px 30px rgba(0,0,0,.28), inset 0 0 0 1px rgba(80,60,30,.08), inset 0 0 ${borde}px rgba(120,86,40,${tinte})`;
}

function Fila({ etiqueta, valor }: { etiqueta: string; valor: ReactNode }) {
  return (
    <div className="fila">
      <span>{etiqueta}</span>
      <span className="tnum">{valor}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estados de cada pieza: al empezar, en la composición final y ya guardada
// ---------------------------------------------------------------------------

function estiloParte(
  parte: Parte,
  final: boolean,
  fase: Fase,
  camara: string,
  altoCarta: number,
): CSSProperties {
  if (fase === 'guardada' && final) {
    const guardada = GUARDADA[parte];
    if (guardada) {
      return guardada;
    }
  }
  if (final) {
    if (parte === 'camara') {
      return { transform: camara };
    }
    return FINAL[parte] ?? {};
  }
  if (parte === 'carta') {
    return { transform: `translateY(${altoCarta}px)` };
  }
  return INICIAL[parte] ?? {};
}

const INICIAL: Partial<Record<Parte, CSSProperties>> = {
  velo: { opacity: 0 },
  cielo: { opacity: 0 },
  mar: { transform: 'translateY(420px)' },
  emerge: { transform: 'translateY(300px) rotate(-14deg)' },
  gavi: { opacity: 0 },
  dice: { opacity: 0 },
  velo2: { opacity: 0 },
  cartela: { opacity: 0 },
  rollo: { opacity: 0 },
  acciones: { opacity: 0 },
  pagina: { transform: 'translateY(900px)', opacity: 0 },
  sello: { opacity: 0 },
  mancha: { opacity: 0 },
  fin: { opacity: 0 },
};

const FINAL: Partial<Record<Parte, CSSProperties>> = {
  ...INICIAL,
  velo: { opacity: 1 },
  cielo: { opacity: 1 },
  mar: { transform: 'none' },
  emerge: { transform: 'none' },
  gavi: { opacity: 1 },
  velo2: { opacity: 1 },
  frente: { opacity: 0 },
  cartela: { opacity: 1 },
  corcho: { opacity: 0 },
  'lacre-izq': { opacity: 0 },
  'lacre-der': { opacity: 0 },
  carta: { transform: 'none' },
  acciones: { opacity: 1 },
};

const aparece = keyframes({ from: { opacity: 0 } });

const GUARDADA: Partial<Record<Parte, CSSProperties>> = {
  acciones: { opacity: 0 },
  cartela: { opacity: 0 },
  carta: { opacity: 0 },
  vuelo: { opacity: 0 },
  pagina: {
    transform: 'none',
    opacity: 1,
    animation: `${aparece} 200ms ease-out`,
  },
  sello: { opacity: 0.95 },
  mancha: { opacity: 0.9 },
  fin: { opacity: 1 },
};

// ---------------------------------------------------------------------------
// Reproductor WAAPI
// ---------------------------------------------------------------------------

type Reproduccion = {
  terminada: Promise<void>;
  temporizadores: number[];
  terminar: () => void;
  cancelar: () => void;
};

/**
 * Crea las animaciones de una línea de tiempo sobre las piezas
 * (`data-parte`) de `raiz`. Arranque en dos tiempos (R2-D §5): lo de
 * después del primer segundo se crea en la tarea siguiente, alineado con el
 * reloj del primero. `desde` adelanta la línea (secuencia abreviada).
 */
function reproducir(
  raiz: HTMLElement,
  pasos: readonly Paso[],
  desde: number,
): Reproduccion {
  const animaciones: Animation[] = [];
  const temporizadores: number[] = [];
  const inicio = performance.now();
  let cancelada = false;
  raiz.classList.add('anim');

  const crear = (lote: readonly Paso[], adelanto: number) => {
    for (const p of lote) {
      const el = raiz.querySelector<HTMLElement>(`[data-parte="${p.parte}"]`);
      if (!el || typeof el.animate !== 'function') {
        continue;
      }
      const a = el.animate(p.fotogramas, {
        delay: p.retraso,
        duration: p.duracion,
        easing: p.curva ?? 'linear',
        fill: 'both',
      });
      if (adelanto > 0) {
        a.currentTime = adelanto;
      }
      if (p.parte === 'frente') {
        // La ola del frente se para en cuanto se apaga (R2-D §5.4).
        a.onfinish = () => {
          el.style.animationPlayState = 'paused';
        };
      }
      animaciones.push(a);
    }
  };

  crear(
    pasos.filter(p => !p.tarde),
    desde,
  );
  const tardias = pasos.filter(p => p.tarde);
  let tardiasCreadas = tardias.length === 0;
  const crearTardias = (adelanto: number) => {
    if (!tardiasCreadas) {
      tardiasCreadas = true;
      crear(tardias, adelanto);
    }
  };
  let resolver: (() => void) | null = null;
  const terminada = new Promise<void>(res => {
    resolver = res;
  });
  const esperar = () =>
    Promise.all(animaciones.map(a => a.finished)).then(
      () => {
        raiz.classList.remove('anim');
        resolver?.();
      },
      () => undefined,
    );
  if (tardias.length > 0) {
    temporizadores.push(
      window.setTimeout(() => {
        if (cancelada) {
          return;
        }
        crearTardias(desde + performance.now() - inicio);
        void esperar();
      }, 0),
    );
  } else {
    void esperar();
  }
  // Por si no hay animaciones (WAAPI no disponible), se da por terminada.
  temporizadores.push(
    window.setTimeout(() => resolver?.(), finDe(pasos) - desde + 200),
  );

  const limpiar = () => {
    temporizadores.forEach(id => window.clearTimeout(id));
    temporizadores.length = 0;
  };
  return {
    terminada,
    temporizadores,
    terminar: () => {
      if (cancelada) {
        return;
      }
      limpiar();
      crearTardias(0);
      animaciones.forEach(a => {
        try {
          a.finish();
        } catch {
          // Una animación sin fin no se puede terminar: se deja.
        }
      });
      raiz.classList.remove('anim');
      resolver?.();
    },
    cancelar: () => {
      cancelada = true;
      limpiar();
      animaciones.forEach(a => a.cancel());
    },
  };
}

/** Vibración tras un gesto (try/catch: en algunos navegadores no está). */
export function vibrar(patron: number[]): void {
  try {
    if (
      typeof navigator !== 'undefined' &&
      'vibrate' in navigator &&
      navigator.userActivation?.hasBeenActive !== false
    ) {
      navigator.vibrate(patron);
    }
  } catch {
    // Sin vibración.
  }
}

function programarVibraciones(
  vibraciones: readonly Vibracion[],
  desde: number,
): number[] {
  return vibraciones.map(v =>
    window.setTimeout(() => vibrar(v.patron), Math.max(0, v.t - desde)),
  );
}

const claseLlegada = css({
  position: 'fixed',
  inset: 0,
  zIndex: LLEGADA_Z_INDEX,
  overflow: 'hidden',
  WebkitTapHighlightColor: 'transparent',
  fontFamily: 'var(--p-font, inherit)',
  '& .escenario': {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transformOrigin: '50% 50%',
  },
  '& .sangra': { left: -2000, top: -2000, right: -2000, bottom: -2000 },
  '&.anim [data-parte]': { willChange: 'transform, opacity' },
  '& .rotulo': {
    fontFamily: SERIF,
    fontStyle: 'italic',
    fontSize: 16,
    color: e.rotulo,
    whiteSpace: 'nowrap',
  },
  '& .cartela': {
    left: 46,
    width: 320,
    textAlign: 'center',
    color: e.papel,
    fontFamily: SERIF,
    '& .cifra': { fontSize: 30, letterSpacing: '.01em', lineHeight: 1.1 },
    '& .linea': {
      fontSize: 14,
      lineHeight: 1.35,
      marginTop: 4,
      color: e.laton2,
      fontStyle: 'italic',
      overflowWrap: 'anywhere',
    },
    '& .regla': {
      position: 'relative',
      height: 7,
      margin: '7px auto 0',
      width: 180,
      background: `linear-gradient(${e.laton},${e.laton}) center/100% 1px no-repeat`,
      '&::before, &::after': {
        content: '""',
        position: 'absolute',
        width: 5,
        height: 5,
        background: e.laton,
        transform: 'rotate(45deg)',
        top: 1,
      },
      '&::before': { left: -6 },
      '&::after': { right: -6 },
    },
  },
  '& .anillo': {
    width: 130,
    height: 130,
    margin: '-65px 0 0 -65px',
    borderRadius: '50%',
    border: `1.5px solid ${e.laton2}`,
    boxShadow:
      '0 0 24px rgba(220,192,138,.45), inset 0 0 18px rgba(220,192,138,.3)',
    opacity: 0,
  },
  '& .rombo': {
    width: 7,
    height: 7,
    margin: -3.5,
    background: e.laton2,
    opacity: 0,
  },
  '& .estrella': { width: 18, height: 18, margin: -9, opacity: 0 },
  '& .marco-carta': {
    paddingLeft: 40,
    paddingRight: 40,
    boxSizing: 'border-box',
  },
  '& .carta': {
    position: 'relative',
    boxSizing: 'border-box',
    width: CARTA.ancho,
    margin: 0,
    padding: '22px 26px 18px',
    background: e.papel,
    backgroundImage: GRANO,
    color: e.tinta,
    borderRadius: 3,
    fontFamily: SERIF,
    '& .cab': {
      fontSize: 14,
      letterSpacing: '.09em',
      fontVariant: 'small-caps',
      textTransform: 'lowercase',
      color: e.tinta2,
      display: 'flex',
      justifyContent: 'space-between',
      gap: 8,
    },
    '& .txt': {
      fontStyle: 'italic',
      fontSize: 18.5,
      lineHeight: 1.48,
      margin: '12px 0 0',
      textWrap: 'pretty',
      overflowWrap: 'anywhere',
      whiteSpace: 'pre-line',
    },
    '& .firma': {
      textAlign: 'right',
      fontStyle: 'italic',
      fontSize: 17,
      margin: '10px 0 0',
      overflowWrap: 'anywhere',
    },
    '& .firma-otra, & .respuesta': {
      fontStyle: 'italic',
      fontSize: 16,
      lineHeight: 1.45,
      margin: '10px 0 0',
      overflowWrap: 'anywhere',
    },
    '& .firma-otra .firma': { display: 'inline', margin: 0, fontSize: 16 },
    '& .respuesta': {
      paddingTop: 8,
      borderTop: '1px dashed rgba(58,46,36,.25)',
      color: e.tinta2,
    },
    '& .pie': {
      marginTop: 14,
      paddingTop: 9,
      borderTop: '1px solid rgba(58,46,36,.25)',
      fontSize: 14,
      letterSpacing: '.09em',
      fontVariant: 'small-caps',
      textTransform: 'lowercase',
      color: e.tinta2,
      textAlign: 'center',
      overflowWrap: 'anywhere',
    },
  },
  '& .rollo': {
    left: CARTA.x,
    width: CARTA.ancho,
    height: 16,
    borderRadius: 8,
    background: `linear-gradient(${e.papel2}, ${e.papel} 45%, ${e.papel2} 80%, #a8977a)`,
    boxShadow: '0 3px 6px rgba(0,0,0,.25)',
  },
  '& .acciones, & .fin': {
    left: CARTA.x,
    width: CARTA.ancho,
    textAlign: 'center',
  },
  '& .fin': { top: 700 },
  '& .sec': {
    display: 'flex',
    justifyContent: 'center',
    gap: 22,
    marginBottom: 12,
    minHeight: 44,
    alignItems: 'center',
  },
  '& .sec button, & .hecho': {
    font: 'inherit',
    fontFamily: SERIF,
    fontSize: 15,
    color: e.papel,
    background: 'none',
    border: 0,
    minHeight: 44,
    padding: '0 4px',
    cursor: 'pointer',
    textDecoration: 'underline',
    textDecorationColor: 'rgba(226,214,189,.4)',
    textUnderlineOffset: 4,
  },
  '& .hecho': { textDecoration: 'none', fontStyle: 'italic', color: e.laton2 },
  '& .pri': {
    width: '100%',
    height: 50,
    borderRadius: 12,
    border: 0,
    background: e.laton2,
    color: '#1f1810',
    fontWeight: 700,
    fontSize: 16,
    cursor: 'pointer',
  },
  '& .responder textarea': {
    boxSizing: 'border-box',
    width: '100%',
    minHeight: 72,
    resize: 'none',
    padding: '10px 12px',
    borderRadius: 8,
    border: `1px solid ${e.laton}`,
    background: e.papel,
    backgroundImage: GRANO,
    color: e.tinta,
    fontFamily: SERIF,
    fontStyle: 'italic',
    fontSize: 16,
    marginBottom: 4,
  },
  '& .pagina': {
    left: 14,
    right: 14,
    top: 230,
    height: 450,
    boxSizing: 'border-box',
    background: e.papel,
    backgroundImage: GRANO,
    color: e.tinta,
    borderRadius: '4px 4px 3px 3px',
    padding: '22px 24px',
    fontFamily: SERIF,
    boxShadow:
      '0 10px 30px rgba(0,0,0,.3), inset 26px 0 26px -24px rgba(90,60,30,.35)',
    '& .cab': {
      fontSize: 14,
      letterSpacing: '.12em',
      fontVariant: 'small-caps',
      textTransform: 'lowercase',
      color: e.tinta2,
    },
    '& h2': {
      fontWeight: 400,
      fontSize: 30,
      lineHeight: 1.15,
      margin: '6px 0 8px',
      overflowWrap: 'anywhere',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    '& .fila': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 12,
      padding: '7px 0',
      borderBottom: '1px solid rgba(58,46,36,.16)',
      fontSize: 15.5,
      '& span:first-child': {
        fontVariant: 'small-caps',
        textTransform: 'lowercase',
        letterSpacing: '.06em',
        color: e.tinta2,
        fontSize: 16,
        flex: 'none',
      },
      '& span:last-child': { textAlign: 'right', minWidth: 0 },
    },
    '& .sello': { right: 18, bottom: 18, width: 120, height: 120 },
    '& .mancha': {
      right: 8,
      bottom: 8,
      width: 140,
      height: 140,
      borderRadius: '50%',
    },
  },
});
